/** @file webZlibCodec: zlib over Compression Streams (Workers, browsers, Node 22). */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { IMAGE_IO_ERRORS } from '../errors/imageIoErrors';
import type { ZlibCodec } from '../types/zlibCodec';
import type { WebStreamsGlobals } from './webStreamsGlobals';

/** Pipes `bytes` through a new `which('deflate')` stream and collects the output. */
async function run(bytes: Uint8Array, which: 'CompressionStream' | 'DecompressionStream'): Promise<Uint8Array> {
  const g = globalThis as unknown as WebStreamsGlobals;
  const Stream = g[which];
  const Response = g.Response;
  if (Stream === undefined || Response === undefined) {
    return throwError(IMAGE_IO_ERRORS.ZLIB_UNAVAILABLE, { missing: Stream === undefined ? which : 'Response' },
      { hint: 'inject a ZlibCodec (e.g. a node:zlib adapter) on runtimes without Compression Streams' });
  }
  const body = new Response(bytes).body;
  if (body === null) return new Uint8Array(0);
  return new Uint8Array(await new Response(body.pipeThrough(new Stream('deflate'))).arrayBuffer());
}

/** Inflates a zlib stream with `DecompressionStream('deflate')`. */
function inflate(data: Uint8Array): Promise<Uint8Array> {
  return run(data, 'DecompressionStream');
}

/** Deflates to a zlib stream with `CompressionStream('deflate')`. */
function deflate(data: Uint8Array): Promise<Uint8Array> {
  return run(data, 'CompressionStream');
}

/** The zlib codec for Workers and other web runtimes; both methods are async and throw `ZLIB_UNAVAILABLE` without the API. */
export const webZlibCodec: ZlibCodec = { inflate, deflate };
